/**
 * Which changes on the proposal card are drawn as the builder's exercise card, and what each card is
 * compared with (ai-plan-builder R15; board U, owner 7 October 2026).
 *
 *   exerciseAlter    the exercise after the change, marked against the exercise as it stands; the
 *                    head says the set count when it moves („4 → 5 serii")
 *   exerciseReplace  the new exercise as it is, with the one it replaces said above it, struck
 *   exerciseAdd      the new exercise, every set new, at the end of its day
 *
 * Everything else keeps its line: a removal, a day, a phase. So does a change the trainer's own edits
 * have overtaken (R18): what it would have written is no longer what the card would compare, and
 * the change will not be applied.
 *
 * Pure: no React, no i18next. The words are `lib/proposalWording.ts`'s.
 */

import type { AiProposalChangeFacts, ExerciseDraft } from '~/entities/plan-draft';

/**
 * What the card compares the exercise with. The same shape `features/plan-day-programming`'s card
 * takes; declared here too because a feature may not import a feature, and the host that hands one
 * to the other is what knows they agree.
 */
export type ExerciseBaseline =
  | { readonly kind: 'previous'; readonly exercise: ExerciseDraft }
  | { readonly kind: 'added' }
  | { readonly kind: 'none' };

export interface ExerciseDrawing {
  /** The exercise as the change writes it. */
  readonly exercise: ExerciseDraft;
  readonly baseline: ExerciseBaseline;
  /** The builder's marker for its place in the day (`2`, `2b`); `null` for a warm-up or when unknown. */
  readonly ordinal: string | null;
  /** The day's own rest, in seconds; `null` for the plan's default. */
  readonly dayRestSeconds: number | null;
  /** A replacement's old exercise, said above the card; `null` for any other change. */
  readonly replaced: ExerciseDraft | null;
}

/** How the change is drawn as a card, or `null` when it keeps its line. */
export function exerciseDrawing(facts: AiProposalChangeFacts, outdated: boolean): ExerciseDrawing | null {
  const { change, before, after, ordinal } = facts;
  const dayRestSeconds = facts.day?.restSeconds ?? null;
  if (outdated || after === null) return null;
  switch (change.kind) {
    case 'exerciseAlter':
      return before === null ? null : { exercise: after, baseline: { kind: 'previous', exercise: before }, ordinal, dayRestSeconds, replaced: null };
    case 'exerciseReplace':
      return { exercise: after, baseline: { kind: 'none' }, ordinal, dayRestSeconds, replaced: before };
    case 'exerciseAdd':
      return { exercise: after, baseline: { kind: 'added' }, ordinal, dayRestSeconds, replaced: null };
    default:
      return null;
  }
}
